// DeviceManagement.jsx



import React, { useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Button,
  TextField,
  Paper
} from '@mui/material';
import { Edit, Delete, Add } from '@mui/icons-material';
import useUserDevices from '../../hooks/useUserDevices';

/**
 * This component handles:
 * 1. Digital locks (with notes, codes, etc.)
 * 2. Modems (with portal links, etc.)
 * 3. Assigning a property address or name to each device
 *
 * Data is stored in Firestore under /users/{uid}/devices
 */
const DeviceManagement = () => {
  const { devices, loading, error, addDevice, updateDevice, deleteDevice } = useUserDevices();

  // New device local form state
  const [newDevice, setNewDevice] = useState({
    name: '',            // e.g. "Back Door Lock", "Modem #1"
    type: '',            // e.g. "Digital Lock", "Modem"
    propertyAddress: '', // e.g. "123 Main St, Anytown"
    notes: '',           // Could store codes for the lock, or anything else
    model: '',           // For modems or camera model
    serial: '',          // Serial # if needed
    portalLink: ''       // For modems or camera
  });

  // If editing an existing device, we store its doc id here
  const [editingDeviceId, setEditingDeviceId] = useState(null);

  /**
   * Handle input changes for the local form
   */
  const handleInputChange = (field, value) => {
    setNewDevice((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * Create or update the device in Firestore
   */
  const handleSaveDevice = async () => {
    try {
      if (editingDeviceId) {
        // Updating existing device
        await updateDevice(editingDeviceId, { ...newDevice });
        setEditingDeviceId(null);
      } else {
        // Adding new device
        await addDevice({ ...newDevice });
      }
      // Clear form
      setNewDevice({
        name: '',
        type: '',
        propertyAddress: '',
        notes: '',
        model: '',
        serial: '',
        portalLink: ''
      });
    } catch (err) {
      console.error(err);
      alert('Failed to save device');
    }
  };

  /**
   * Populate form with existing device data for editing
   */
  const handleEditDevice = (device) => {
    setEditingDeviceId(device.id);
    setNewDevice({
      name: device.name || '',
      type: device.type || '',
      propertyAddress: device.propertyAddress || '',
      notes: device.notes || '',
      model: device.model || '',
      serial: device.serial || '',
      portalLink: device.portalLink || ''
    });
  };

  /**
   * Delete a device
   */
  const handleDeleteDevice = async (deviceId) => {
    if (window.confirm('Are you sure you want to delete this device?')) {
      try {
        await deleteDevice(deviceId);
      } catch (err) {
        console.error(err);
        alert('Failed to delete device');
      }
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ marginBottom: '1rem' }}>
        Device Management
      </Typography>
      {loading && <Typography>Loading devices...</Typography>}
      {error && <Typography color="error">{error}</Typography>}

      <Paper sx={{ padding: '1rem', marginBottom: '1rem' }}>
        {/* Form for adding/updating a device */}
        <Box display="flex" flexWrap="wrap" gap="1rem" marginBottom="1rem">
          <TextField
            label="Device Name"
            value={newDevice.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            fullWidth
          />
          <TextField
            label="Device Type (e.g. Digital Lock, Modem)"
            value={newDevice.type}
            onChange={(e) => handleInputChange('type', e.target.value)}
            fullWidth
          />
          <TextField
            label="Property Address"
            value={newDevice.propertyAddress}
            onChange={(e) => handleInputChange('propertyAddress', e.target.value)}
            fullWidth
          />
          <TextField
            label="Notes (Lock Codes, etc.)"
            value={newDevice.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            fullWidth
          />
          <TextField
            label="Model"
            value={newDevice.model}
            onChange={(e) => handleInputChange('model', e.target.value)}
            fullWidth
          />
          <TextField
            label="Serial Number"
            value={newDevice.serial}
            onChange={(e) => handleInputChange('serial', e.target.value)}
            fullWidth
          />
          <TextField
            label="Portal Link"
            value={newDevice.portalLink}
            onChange={(e) => handleInputChange('portalLink', e.target.value)}
            fullWidth
          />

          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={handleSaveDevice}
          >
            {editingDeviceId ? 'Update' : 'Add'}
          </Button>
        </Box>
      </Paper>

      {/* Table of existing devices */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Address</TableCell>
            <TableCell>Notes</TableCell>
            <TableCell>Model</TableCell>
            <TableCell>Serial</TableCell>
            <TableCell>Portal Link</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {devices.map((device) => (
            <TableRow key={device.id}>
              <TableCell>{device.name}</TableCell>
              <TableCell>{device.type}</TableCell>
              <TableCell>{device.propertyAddress}</TableCell>
              <TableCell>{device.notes}</TableCell>
              <TableCell>{device.model}</TableCell>
              <TableCell>{device.serial}</TableCell>
              <TableCell>
                {device.portalLink ? (
                  <a href={device.portalLink} target="_blank" rel="noopener noreferrer">
                    {device.portalLink}
                  </a>
                ) : (
                  'N/A'
                )}
              </TableCell>
              <TableCell>
                <IconButton color="primary" onClick={() => handleEditDevice(device)}>
                  <Edit />
                </IconButton>
                <IconButton color="error" onClick={() => handleDeleteDevice(device.id)}>
                  <Delete />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {devices.length === 0 && !loading && (
            <TableRow>
              <TableCell colSpan={8}>No devices found. Add one above.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
};

export default DeviceManagement;
